import React from 'react';
import { motion } from 'framer-motion';
import { Check, Wrench, Search, Zap, Cpu, AlertTriangle, FileText, ShieldCheck, BadgeCheck, ClipboardCheck, Handshake, ShieldAlert } from 'lucide-react';
export function MaintenanceServices() {
  const inspectionItems = ['Airframe inspection', 'Engine inspection', 'Propeller inspection', 'Avionics operational check', 'Flight control inspection', 'Fuel system inspection', 'Electrical system check', 'Interior and exterior inspection', 'Control surface check', 'Safety and compliance review', 'Lubrication as required', 'Light system inspection', 'Pilot and static system check', 'ELT operational check', 'Placards and markings check', 'Weight and balance review', 'Detailed logbook entry', 'Discrepancy review', 'Return to service approval'];
  const lineServices = [{
    icon: Wrench,
    title: 'A&P Certified Line Maintenance'
  }, {
    icon: Search,
    title: 'Pre and Post Flight Inspections'
  }, {
    icon: Zap,
    title: 'Engines and APU Services'
  }, {
    icon: Cpu,
    title: 'Avionics Support'
  }, {
    icon: AlertTriangle,
    title: 'Discrepancy Troubleshooting'
  }, {
    icon: FileText,
    title: 'Logbook Entry and Return to Service'
  }];
  return <section id="maintenance" className="py-24 bg-[#0d1323] relative border-b border-white/5" data-id="element-322" data-mp="13xzmf0s49stl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-323" data-mp="z06g87qtcytb">
        <div className="text-center mb-12" data-id="element-324" data-mp="1dob37nwwfwhw">
          <span className="text-gold text-sm font-bold tracking-widest uppercase mb-3 block" data-id="element-325" data-mp="1dn9isstztxni">
            A&P Services
          </span>
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mb-4 uppercase" data-id="element-326" data-mp="18ravl8keiloo">
            Certified. Experienced. Dependable.
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto" data-id="element-327" data-mp="1iof9ym809cux">
            Professional line maintenance services that keep your aircraft safe,
            compliant and ready to fly.
          </p>
        </div>

        {/* Highlights */}
        <div className="flex flex-wrap justify-center gap-4 md:gap-8 mb-16" data-id="element-328" data-mp="1uncahpivl41h">
          {['A&P Certified Mechanics', '24/7 Availability', 'Safety Focused', 'Quality Workmanship'].map((badge, idx) => <div key={idx} className="bg-navy border border-white/10 rounded-full px-6 py-2 flex items-center" data-id="element-329" data-mp="28gabbre37ocf" data-mpi={idx}>
              <ShieldCheck className="w-4 h-4 text-gold mr-2" data-id="element-330" data-mp="2ifj0m6y0cesg" />
              <span className="text-sm font-medium text-slate-300 uppercase tracking-wider" data-id="element-331" data-mp="2ig13vnj37t9v">
                {badge}
              </span>
            </div>)}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8" data-id="element-332" data-mp="o2joinsu62px">
          {/* Left Column: 100 Hour */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="bg-navy rounded-2xl p-8 border border-white/5" data-id="element-333" data-mp="t0l8day3662g">
            <h3 className="text-2xl font-heading font-bold text-white mb-2 uppercase" data-id="element-334" data-mp="2sjg5k3k987rq">
              100 Hour Inspections
            </h3>
            <p className="text-slate-400 text-sm mb-8 pb-6 border-b border-white/10" data-id="element-335" data-mp="3p85o7w3ph4er">
              On Smaller Airplanes. Performed in accordance with FAR 43 Appendix
              D and manufacturer guidelines.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-6" data-id="element-336" data-mp="3gavc68j2db8s">
              {inspectionItems.map((item, idx) => <div key={idx} className="flex items-start" data-id="element-337" data-mp="24u54ekyzsbd2" data-mpi={idx}>
                  <Check className="w-5 h-5 text-gold mr-3 shrink-0 mt-0.5" data-id="element-338" data-mp="24riy0onu81wf" />
                  <span className="text-slate-300 text-sm" data-id="element-339" data-mp="1zsxxsp8l5i7z">{item}</span>
                </div>)}
            </div>
          </motion.div>

          {/* Right Column: Line Maintenance */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="flex flex-col gap-4" data-id="element-340" data-mp="3sji1jn6umeoz">
            <div className="bg-navy rounded-2xl p-8 border border-white/5 mb-2" data-id="element-341" data-mp="b7pdjmt5alu4">
              <h3 className="text-2xl font-heading font-bold text-white uppercase" data-id="element-342" data-mp="favimr4iu6z0">
                Line Maintenance Services
              </h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 flex-grow" data-id="element-343" data-mp="6a7bmgdjp6vm">
              {lineServices.map((service, idx) => <div key={idx} className="bg-navy rounded-2xl p-6 border border-white/5 hover:border-gold/30 transition-colors flex flex-col items-center text-center justify-center group" data-id="element-344" data-mp="2m0a49ugg023d" data-mpi={idx}>
                  <div className="w-12 h-12 rounded-full bg-navy-light flex items-center justify-center mb-4 group-hover:shadow-gold-glow transition-all" data-id="element-345" data-mp="2eic8stv3irj5">
                    <service.icon className="w-6 h-6 text-gold" data-id="element-346" data-mp="2shlegzloe38v" />
                  </div>
                  <h4 className="text-white font-heading font-medium tracking-wide uppercase text-sm" data-id="element-347" data-mp="2nkbptm641vm3">
                    {service.title}
                  </h4>
                </div>)}
            </div>
          </motion.div>
        </div>

        {/* Bottom Commitments */}
        <div className="mt-16 pt-12 border-t border-white/5 grid grid-cols-2 md:grid-cols-4 gap-6 text-center" data-id="element-348" data-mp="29kaq4a1l0u87">
          {[{
          icon: BadgeCheck,
          text: 'Certified A&P Professionals'
        }, {
          icon: ClipboardCheck,
          text: 'FAA Compliant'
        }, {
          icon: Handshake,
          text: 'Transparent & Honest'
        }, {
          icon: ShieldAlert,
          text: 'Safety Is Our Priority'
        }].map((item, idx) => <div key={idx} className="flex flex-col items-center" data-id="element-349" data-mp="3lvo1ohpk33ax" data-mpi={idx}>
              <div className="w-10 h-10 rounded-full bg-navy border border-gold/30 flex items-center justify-center mb-3" data-id="element-350" data-mp="3lyab6mw0bsvs">
                <item.icon className="w-5 h-5 text-gold" data-id="element-351" data-mp="2t7s65gbq3hfq" />
              </div>
              <span className="text-slate-400 text-sm font-medium uppercase tracking-wider" data-id="element-352" data-mp="2y6bvlqpizbcu">
                {item.text}
              </span>
            </div>)}
        </div>
      </div>
    </section>;
}